/**
 * Shared Statistics card headline — matches distribution donut CardTitle.
 * @see DistributionPieLabelChart CardTitle (text-xs, IBM Plex Sans, muted)
 */

import React from "react";
import { Text, type TextProps } from "@chakra-ui/react";
import { useTheme } from "next-themes";

export const STATS_FONT_FAMILY = "var(--font-primary)";

/** Tailwind muted-foreground (light) */
export const STATS_CARD_HEADLINE_COLOR_LIGHT = "#6b7280";
/** Section headlines on dark cards — white body copy (not table headers). */
export const STATS_CARD_HEADLINE_COLOR_DARK = "#ffffff";

/** Donut center labels on dark cards — white body copy. */
export const STATS_DONUT_CENTER_TEXT_FILL_DARK = "#ffffff";

export function useStatsCardHeadlineColor(): string {
  const { resolvedTheme } = useTheme();
  return resolvedTheme === "dark"
    ? STATS_CARD_HEADLINE_COLOR_DARK
    : STATS_CARD_HEADLINE_COLOR_LIGHT;
}

/** SVG fill for donut center value + subtitle (white in dark, muted in light). */
export function useStatsMutedTextFill(): string {
  const { resolvedTheme } = useTheme();
  return resolvedTheme === "dark"
    ? STATS_DONUT_CENTER_TEXT_FILL_DARK
    : STATS_CARD_HEADLINE_COLOR_LIGHT;
}

export const statsCardHeadlineTextProps: Omit<TextProps, "color" | "children"> = {
  fontSize: "xs",
  fontFamily: STATS_FONT_FAMILY,
  fontWeight: "medium",
  textTransform: "uppercase",
  letterSpacing: "wide",
  lineHeight: "1.2",
};

/** shadcn CardTitle classes for distribution donuts */
export const statsCardHeadlineClassName =
  "text-xs font-medium uppercase tracking-wide text-muted-foreground dark:text-white font-[family-name:var(--font-primary)]";

/** Muted table/body copy on statistics cards (light gray-500, dark white). */
export const STATS_MUTED_TEXT_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500 dark:text-white";

export function StatisticsCardHeadline({
  children,
  ...props
}: TextProps) {
  const color = useStatsCardHeadlineColor();
  return (
    <Text {...statsCardHeadlineTextProps} color={color} {...props}>
      {children}
    </Text>
  );
}
